import { useEffect } from "react";
import { Edge, Node } from "@xyflow/react";
import {
  uuid,
  selectedNodesAtom,
  nodesAtom,
  isViewOnlyModeAtom,
  edgesAtom,
  saveCanvasSetter,
} from "@zapier/canvas-state";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

export function useCopyPaste() {
  const selectedNodes = useAtomValue(selectedNodesAtom);
  const [nodes, setNodes] = useAtom(nodesAtom);
  const [edges, setEdges] = useAtom(edgesAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  useEffect(() => {
    if (isViewOnlyMode) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if we're in a text input or contenteditable element
      const activeElement = document.activeElement;

      // This logic should only execute when copy/pasting from the body, node or multi select panel
      const activeCanCopyPasteElements = activeElement?.matches(
        "body, .react-flow__nodesselection-rect, .react-flow__node",
      );
      if (!activeCanCopyPasteElements) return;

      // Check for Copy (Ctrl+C or Cmd+C)
      if ((event.ctrlKey || event.metaKey) && event.key === "c") {
        if (selectedNodes.length > 0) {
          event.preventDefault();

          const nodesToCopy = selectedNodes
            .filter((node) => node.type !== "zapStep")
            .map((node) => ({
              ...node,
              selected: false,
            }));

          const edgesToCopy = edges
            .filter(
              (edge) =>
                edge.type !== "zapStepEdge" &&
                selectedNodes.some((node) => node.id === edge.source) &&
                selectedNodes.some((node) => node.id === edge.target),
            )
            .map((edge) => ({
              ...edge,
              selected: false,
            }));

          navigator.clipboard
            .writeText(
              JSON.stringify({
                nodes: nodesToCopy,
                edges: edgesToCopy,
              }),
            )
            .catch((err) => console.error("Failed to copy to clipboard:", err));
        }
      }
      // Check for Paste (Ctrl+V or Cmd+V)
      else if ((event.ctrlKey || event.metaKey) && event.key === "v") {
        // Try to read clipboard as text and parse as JSON
        // If clipboard contains images or other non-text content, this will fail silently
        // and useImageInsert will handle it via the paste event
        navigator.clipboard
          .readText()
          .then((text) => {
            if (!text) return;

            event.preventDefault();
            const idMapping = new Map<string, string>();
            try {
              let newEdges: Edge[] = [];
              let newNodes: Node[] = [];
              const { nodes: pastedNodes, edges: pastedEdges } =
                JSON.parse(text);

              if (Array.isArray(pastedNodes)) {
                // Create new IDs for all pasted nodes
                pastedNodes.forEach((node) => {
                  const newId = uuid();
                  idMapping.set(node.id, newId);
                });

                // Map through nodes, updating IDs and parentIds
                const remappedNodes = pastedNodes.map((node) => ({
                  ...node,
                  id: idMapping.get(node.id)!,
                  parentId: node.parentId
                    ? idMapping.get(node.parentId)
                    : undefined,
                  position: {
                    x: node.position.x + 40,
                    y: node.position.y + 40,
                  },
                }));

                const unselectedNodes = nodes.map((node) => ({
                  ...node,
                  selected: false,
                }));
                newNodes = [...unselectedNodes, ...remappedNodes];
                setNodes(newNodes);
              }

              if (Array.isArray(pastedEdges)) {
                const remappedEdges = pastedEdges.map((edge) => ({
                  ...edge,
                  id: uuid(),
                  source: idMapping.get(edge.source) || edge.source,
                  target: idMapping.get(edge.target) || edge.target,
                }));

                const unselectedEdges = edges.map((edge) => ({
                  ...edge,
                  selected: false,
                }));
                newEdges = [...unselectedEdges, ...remappedEdges];
                setEdges(newEdges);
              }

              // Save nodes and edges
              saveToBackend({
                nodes: newNodes,
                edges: newEdges,
              });
            } catch (err) {
              // Expected: clipboard contains non-JSON content
              // useImageInsert handles image paste via paste event
              if (err instanceof SyntaxError || err instanceof DOMException) {
                return; // Silent fail for expected cases
              }
              console.error("Unexpected clipboard error:", err);
            }
          })
          .catch((err) => {
            // Expected: clipboard contains non-text content (images, etc.)
            // useImageInsert handles image paste via paste event
            if (err instanceof DOMException) {
              return;
            }
            console.error("Unexpected clipboard read error:", err);
          });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedNodes,
    nodes,
    setNodes,
    edges,
    setEdges,
    isViewOnlyMode,
    saveToBackend,
  ]);
}
